import type { OnDestroy } from '@angular/core';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import type { Candidate } from '@recruitee/candidate-types';
import { isCandidateDataVisible } from '@recruitee/candidate-util';
import { TranslatePipe } from '@recruitee/i18n';
import type { RtButtonVariant, RtInfoboxButtonVariant } from '@recruitee/user-interface';
import { ButtonComponent, ButtonLoadingDirective, IconComponent } from '@recruitee/user-interface';

import type { ConfirmDialogResult } from './confirm-reveal-dialog/confirm-reveal-dialog.component';
import { revealConfirmationFactory } from './reveal-confirmation-factory';

@Component({
  selector: 'rt-reveal-button',
  template: `
    <rt-button
      [variant]="variant()"
      [size]="size()"
      [loading]="isLoading()"
      (click)="onToggleDataVisibility()"
    >
      <rt-icon [name]="visibilityIcon()" prefix></rt-icon>
      {{ buttonCopy() | translate }}
    </rt-button>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonComponent, ButtonLoadingDirective, IconComponent, TranslatePipe],
})
export class RevealButtonComponent implements OnDestroy {
  private readonly revealConfirmation$ = revealConfirmationFactory();

  public isRevealed = input.required<boolean, Candidate>({
    transform: (candidate: Candidate) => isCandidateDataVisible(candidate),
    alias: 'candidate',
  });

  public skipConfirmation = input<boolean, boolean | string>(false, {
    transform: booleanAttribute,
  });

  public variant = input<RtButtonVariant | RtInfoboxButtonVariant>('tertiary');

  public size = input<'small-x' | 'small' | 'medium'>('medium');

  public buttonCopy = computed(() =>
    !this.isRevealed()
      ? 'app.candidate.anonymization.reveal_for_me'
      : 'app.candidate.anonymization.hide_for_me',
  );

  public visibilityIcon = computed(() => (!this.isRevealed() ? 'visibility' : 'visibility-off'));

  public toggleDataVisibility = output<boolean>();

  public saveDontAskAgain = output<void>();

  public isLoading = signal<boolean>(false);

  private ngOnDestroy$ = new Subject<void>();

  private markLoadedOnCandidateChange = toObservable(this.isRevealed)
    .pipe(takeUntilDestroyed())
    .subscribe(() => this.isLoading.set(false));

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public onToggleDataVisibility(): void {
    this.isLoading.set(true);

    if (!this.isRevealed()) {
      if (this.skipConfirmation()) {
        this.toggleDataVisibility.emit(true);
        return;
      }

      this.revealConfirmation$()
        .pipe(takeUntil(this.ngOnDestroy$))
        .subscribe(result => this.handleRevealConfirmationResult(result));
    } else {
      this.toggleDataVisibility.emit(false);
    }
  }

  private handleRevealConfirmationResult(result: ConfirmDialogResult): void {
    this.isLoading.set(false);

    if (result.confirmed) {
      this.toggleDataVisibility.emit(true);
    }

    if (result.dontAskAgain) {
      this.saveDontAskAgain.emit();
    }
  }
}
